import React from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, LogOut, Bell, Settings, Download, Menu, ChevronLeft, ChevronRight } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Header({ 
  onSearch, 
  lastUpdated, 
  currentPlatform,
  onExport,
  wsStatus = "connecting",
  audioEnabled = false,
  setAudioEnabled,
  sidebarCollapsed,
  setSidebarCollapsed
}) {
  const { user, logout } = useAuth();
  
  const statusColor = {
    connected: "bg-emerald-500",
    connecting: "bg-amber-500 animate-pulse",
    disconnected: "bg-red-500"
  }[wsStatus] || "bg-slate-400";

  return (
    <header className="h-14 bg-white border-b border-slate-200 px-5 flex items-center gap-4" data-testid="dashboard-header">
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 -ml-2 text-slate-500 hover:text-slate-900"
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
      >
        {sidebarCollapsed ? <Menu className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
      </Button>
      {/* Removed redundant logo to unify branding in the Sidebar */}

      <div className="ml-auto flex items-center gap-3">
        <div className="relative w-72 hidden md:block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search posts, authors, sites…"
            onChange={(e) => onSearch && onSearch(e.target.value)}
            className="pl-9 h-9 rounded-sm border-slate-300 text-sm"
            data-testid="global-search"
          />
        </div>

        <div className="hidden md:flex items-center gap-2 text-[11px] font-mono text-slate-500 border-r pr-3 h-6">
          <span className={`inline-block h-2 w-2 rounded-full ${statusColor}`} />
          <span className="capitalize">{wsStatus} · {lastUpdated || "—"}</span>
        </div>

        <Button 
          variant="outline" 
          size="sm" 
          className="h-9 gap-2 border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-[#2563eb] hover:border-[#2563eb]/30" 
          onClick={onExport}
          title="Download Daily Strategic Intelligence Report"
        >
          <Download className="h-4 w-4" />
          <span className="hidden lg:inline text-xs font-semibold tracking-tight">Export Strategic Briefing</span>
        </Button>

        <Button 
          variant={audioEnabled ? "default" : "ghost"} 
          size="icon" 
          className={`h-9 w-9 rounded-sm transition-colors ${audioEnabled ? "bg-red-600 hover:bg-red-700 text-white" : ""}`}
          onClick={() => setAudioEnabled && setAudioEnabled(!audioEnabled)}
          title={audioEnabled ? "Mute Audio Alerts" : "Enable Audio Alerts (Required for sound)"}
        >
          <Bell className={`h-4 w-4 ${audioEnabled ? "animate-bounce" : ""}`} />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 h-9 px-2 border border-slate-200 hover:bg-slate-50 rounded-sm"
              data-testid="user-menu-btn"
            >
              <div className="h-6 w-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-[10px] font-semibold">
                {user?.name?.split(" ").map((w) => w[0]).slice(0, 2).join("") || "AN"}
              </div>
              <div className="text-left hidden md:block">
                <div className="text-xs font-semibold leading-none">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">{user?.role}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-sm w-52">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Signed in as</DropdownMenuLabel>
            <DropdownMenuItem disabled className="font-mono text-xs">{user?.email}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem><Settings className="h-3.5 w-3.5 mr-2" /> Preferences</DropdownMenuItem>
            <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-btn">
              <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
